/**
 * useShopTimer - Hook für Shop-Timer Logik
 *
 * Extrahiert aus ShopWithTimer.js für bessere Wartbarkeit.
 * Verwaltet den Countdown und Timer-Sounds.
 */

import { useState, useEffect } from 'react';
import soundService from '../soundService';

/**
 * @param {Object} config - Konfiguration
 * @param {number} config.timeLimit - Zeit in Sekunden (default 60)
 * @param {boolean} config.isReady - Ob Spieler fertig ist
 * @param {function} config.onReady - Callback wenn Zeit abgelaufen
 * @param {function} config.onTimeUp - Callback für Time-Up Event
 */
export function useShopTimer({
  timeLimit = 60,
  isReady,
  onReady,
  onTimeUp,
}) {
  const [timeLeft, setTimeLeft] = useState(timeLimit);

  useEffect(() => {
    if (isReady) return;

    if (timeLeft <= 0) {
      onReady?.();
      onTimeUp?.();
      return;
    }

    // Play timer warning sound at 10 seconds
    if (timeLeft === 10) {
      soundService.play('timer_warning');
    }

    // Play tick sound in final 5 seconds
    if (timeLeft <= 5 && timeLeft > 0) {
      soundService.play('timer_tick');
    }

    const timer = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, isReady, onReady, onTimeUp]);

  // Reset timer when timeLimit changes
  useEffect(() => {
    setTimeLeft(timeLimit);
  }, [timeLimit]);

  // Format time as MM:SS
  const formattedTime = `${Math.floor(timeLeft / 60)}:${(timeLeft % 60).toString().padStart(2, '0')}`;

  // Warning state (last 10 seconds)
  const isWarning = timeLeft <= 10;

  return {
    timeLeft,
    formattedTime,
    isWarning,
  };
}

export default useShopTimer;
